import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Eye, EyeOff, Loader2, AlertCircle, ShieldAlert } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { authAPI } from '../lib/api';

// Bloqueia o acesso ao app até o usuário trocar uma senha marcada como
// must_change_password (owner recém-semeado ou senha definida por um
// admin) — ver docs/DocsRevisaoGeral/PlanosCorrecao/Lote2-Enforcement/
// 04-rate-limit-login-e-hardening-owner.md. Roteada por ProtectedRoute em
// App.js, que redireciona pra cá sempre que user.must_change_password.
export default function ForcePasswordChangePage() {
  const { theme, user, patchUser, logout } = useApp();
  const primary = theme?.primary || '#2563eb';
  const navigate = useNavigate();

  const [form, setForm] = useState({ current_password: '', password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      setError('Informe sua senha atual.');
      return;
    }
    if (form.password.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (form.password !== form.confirm) {
      setError('As senhas não coincidem.');
      return;
    }
    if (form.password === form.current_password) {
      setError('A nova senha deve ser diferente da atual.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authAPI.changePassword(form.current_password, form.password);
      patchUser({ must_change_password: false });
      navigate('/app', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      setError(err.response?.data?.error || (status === 401 ? 'Senha atual incorreta.' : 'Falha ao trocar a senha.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'var(--bg)',
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -20%, ${primary}22 0%, transparent 70%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-3">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
            >
              <Radio size={26} className="text-white" />
            </div>
            <span className="font-display font-black text-xl tracking-tight text-[var(--text-primary)]">
              OLT MANAGER
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--border-mid)] bg-[var(--surface)] shadow-2xl p-8">
          <div className="mb-5 flex items-start gap-3 rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-sm text-amber-500">
            <ShieldAlert size={18} className="shrink-0 mt-0.5" />
            <span>
              Sua senha foi definida por um administrador (ou é a senha inicial da instância).
              Defina uma nova senha para continuar — o acesso fica bloqueado até a troca.
            </span>
          </div>

          <h1 className="text-xl font-bold text-[var(--text-primary)] mb-6">Trocar senha</h1>

          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
              <AlertCircle size={15} className="shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                Senha atual
              </label>
              <input
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                Nova senha
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full h-11 px-4 pr-11 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                Confirmar nova senha
              </label>
              <input
                type={showPass ? 'text' : 'password'}
                autoComplete="new-password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                placeholder="Repita a senha"
                className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Salvando...' : 'Trocar senha e continuar'}
            </button>
          </form>

          <button
            type="button"
            onClick={logout}
            className="mt-6 w-full text-center text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            Sair
          </button>
        </div>
      </motion.div>
    </div>
  );
}
